import React, { useEffect, useRef, useState } from 'react';
import App, { AppContext, AppProps } from 'next/app';
import Script from 'next/script';
import { I18nextProvider, initI18n, Messages } from '@wix/wix-i18n-config';
import { WixDesignSystemProvider } from '@wix/design-system';
import { RootEssentialsProvider } from '@wix/fe-essentials-standalone/react';
import { createEssentials } from '@wix/fe-essentials-standalone';
import type { ExperimentsBag } from '@wix/wix-experiments';
import { Article, Category } from '@wix/answers-api';
import { HEADER_FOOTER_SUPPORT_LANGUAGES, LOCALES } from '../constants';
import { PageTemplate, PageTemplateData } from '../components/PageTemplate';
import { Context, answersApi } from '../context';
import { PageType, CustomWindow, PageSeo } from '../types';
import { Error } from '../components/Error';
import { NotificationBar } from '../components/NotificationBar';
import { GlobalLoader } from '../components/GlobalLoader';
import HistoryProvider from '../hooks/useRouteHistory';
import {
  hasAnswersSSO,
  isPersonalPage,
  isUserLoggedIn,
  loginWithAnswersSSO,
} from '../utils/login';
import '../styles/globals.scss';
import '@wix/design-system/styles.global.css';
import 'normalize.css/normalize.css';

type PageData = {
  article?: Article;
  category?: Category;
};

type PageProps = {
  pageData: PageData;
  pageType: PageType;
  itemId?: string;
  itemType?: string;
  isMobile: boolean;
  isTablet: boolean;
  categoriesTree: Category[];
  experiments: ExperimentsBag;
  pageSeo: PageSeo;
  hasError?: boolean;
  messageError?: string;
  statusCode?: number;
};

type CustomAppProps = AppProps & {
  locale: string;
  messages: Messages;
  experiments: ExperimentsBag;
  pageProps: PageProps;
};

declare const window: CustomWindow;

const CustomApp = ({
  Component,
  pageProps,
  locale,
  messages,
}: CustomAppProps) => {
  const i18n = initI18n({
    locale,
    messages,
  });

  const {
    pageType,
    itemType,
    isMobile,
    categoriesTree,
    experiments,
    pageSeo,
    hasError,
    messageError,
    statusCode,
    itemId,
    pageData,
    isTablet,
    isWixStudioUser,
  } = pageProps;

  const first = useRef(true);

  const [pageTemplateData, setPageTemplateData] = useState<PageTemplateData>({
    pageData,
    itemId,
    itemType,
    hasError: hasError || false,
  });

  const essentials = createEssentials({
    environment: {
      language: locale,
      artifactId: 'kb-client-nextjs',
      version: '0.1.0',
    },
    experiments: { bag: pageProps.experiments },
    fedops: {},
    httpClient: {
      baseURL: `/${locale}/`,
    },
  });

  useEffect(() => {
    if (window.wixRecorder) {
      window.wixRecorder.addLabel('new-kb');
    }

    if (isLoginRequired(pageType)) {
      loginWithAnswersSSO(location.href, locale);
    }
  }, [locale, pageType]);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setPageTemplateData({
      pageData,
      itemId,
      itemType,
      hasError: hasError || false,
    });
  }, [pageData, itemId, itemType, hasError]);

  return (
    <Context.Provider
      value={{
        answersApi,
        isMobile,
        isTablet,
        experiments,
        categoriesTree,
        isWixStudioUser,
        pageTemplateData,
        setPageTemplateData,
      }}
    >
      <HistoryProvider>
        <RootEssentialsProvider essentials={essentials}>
          <I18nextProvider i18n={i18n}>
            <WixDesignSystemProvider features={{ newColorsBranding: true }}>
              <NotificationBar pageType={pageType} />
              <PageTemplate pageType={pageType} pageSeo={pageSeo}>
                <GlobalLoader />
                {hasError ? (
                  <Error
                    statusCode={statusCode as number}
                    messageError={messageError}
                  />
                ) : isLoginRequired(pageType) ? (
                  <></>
                ) : (
                  <Component {...pageProps} />
                )}
              </PageTemplate>
              <Script
                src="https://static.parastorage.com/unpkg-semver/wix-recorder@^1/app.bundle.min.js"
                strategy="lazyOnload"
              />
              <Script src="https://apps.wix.com/answers-wix-integration/cookie-policy/consent-manager.js" />
              <Script src="https://apps.wix.com/answers-wix-integration/cookie-policy/tag-manager.js" />
              <Script
                src={`https://apps.wix.com/answers-wix-integration/cookie-policy/${
                  HEADER_FOOTER_SUPPORT_LANGUAGES.includes(locale as string)
                    ? locale
                    : LOCALES.EN
                }/setup.js`}
                strategy="lazyOnload"
              />
            </WixDesignSystemProvider>
          </I18nextProvider>
        </RootEssentialsProvider>
      </HistoryProvider>
    </Context.Provider>
  );
};

CustomApp.getInitialProps = async (context: AppContext) => {
  let { locale } = context.router;
  if (!Object.values(LOCALES).includes(locale as string)) {
    locale = LOCALES.EN;
  }
  const { pageProps } = await App.getInitialProps(context);
  const messages = await import(`../locales/messages_${locale}.json`);
  return { pageProps, locale, messages };
};

const isLoginRequired = (pageType: PageType) =>
  typeof window !== 'undefined' &&
  isPersonalPage(pageType) &&
  (!isUserLoggedIn() || !hasAnswersSSO());

export default CustomApp;
